import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { expenseService } from '../../services/expenseService';
import { chartOfAccountService } from '../../services/chartOfAccountService';
import bankAccountService from '../../services/bankAccountService';
import { vendorService } from '../../services/vendorService';
import { CreateExpenseData, EXPENSE_PAYMENT_METHODS } from '../../types/expense';
import { formatCurrency } from '../../utils/formatters';

interface ExpenseFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const ExpenseForm: React.FC<ExpenseFormProps> = ({ onSuccess, onCancel }) => {
  const queryClient = useQueryClient();

  const { data: expenseAccountsResponse, isLoading: expenseAccountsLoading } = useQuery({
    queryKey: ['chart-of-accounts', 'EXPENSE'],
    queryFn: () => chartOfAccountService.getChartOfAccounts({ accountType: 'EXPENSE', isActive: true, limit: 500 }),
  });

  const { data: bankAccountsResponse, isLoading: bankAccountsLoading } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => bankAccountService.getBankAccounts({ isActive: true }),
  });

  const { data: vendorsResponse } = useQuery({
    queryKey: ['vendors', 'all'],
    queryFn: () => vendorService.getVendors({ limit: 500 }),
  });

  const payableBankAccounts = (bankAccountsResponse?.accounts || []).filter(account => !!account.glAccount);
  const unlinkedBankAccountCount = (bankAccountsResponse?.accounts?.length || 0) - payableBankAccounts.length;

  const [formData, setFormData] = useState<CreateExpenseData>({
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    expenseAccountId: '',
    bankAccountId: '',
    vendorId: '',
    payeeName: '',
    paymentMethod: 'cash',
    reference: '',
    notes: '',
  });

  const [errors, setErrors] = useState<string[]>([]);

  const createExpenseMutation = useMutation({
    mutationFn: expenseService.createExpense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      onSuccess();
    },
    onError: (error: any) => {
      console.error('Expense creation failed:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to create expense';
      setErrors([errorMessage]);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = expenseService.validateExpenseData(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    createExpenseMutation.mutate({
      ...formData,
      vendorId: formData.vendorId || undefined,
      payeeName: formData.payeeName || undefined,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value,
    }));

    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px', background: '#f4efe2',
    border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
    fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
    outline: 'none', colorScheme: 'light' as any, boxSizing: 'border-box' as const,
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
    fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 6,
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px 16px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033', margin: 0 }}>
          Record Direct Expense
        </h3>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '24px 28px' }}>
        {errors.length > 0 && (
          <div style={{ background: 'rgba(201,90,90,0.08)', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 4, padding: 12 }}>
            <ul style={{ margin: 0, paddingLeft: 16, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#c95a5a' }}>
              {errors.map((error, index) => <li key={index}>{error}</li>)}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Amount <span style={{ color: '#c95a5a' }}>*</span></label>
              <input
                type="number"
                name="amount"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={handleInputChange}
                style={inputStyle}
                required
              />
            </div>

            <div>
              <label style={labelStyle}>Date <span style={{ color: '#c95a5a' }}>*</span></label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                style={inputStyle}
                required
              />
            </div>

            <div>
              <label style={labelStyle}>Expense Account <span style={{ color: '#c95a5a' }}>*</span></label>
              <select
                name="expenseAccountId"
                value={formData.expenseAccountId}
                onChange={handleInputChange}
                style={{ ...inputStyle, cursor: 'pointer' }}
                required
                disabled={expenseAccountsLoading}
              >
                <option value="">Select an expense account</option>
                {expenseAccountsResponse?.data?.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.code} - {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Paid From <span style={{ color: '#c95a5a' }}>*</span></label>
              <select
                name="bankAccountId"
                value={formData.bankAccountId}
                onChange={handleInputChange}
                style={{ ...inputStyle, cursor: 'pointer' }}
                required
                disabled={bankAccountsLoading}
              >
                <option value="">Select a bank account</option>
                {payableBankAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.accountName} - {account.bankName} ({formatCurrency(account.balance)})
                  </option>
                ))}
              </select>
              {!bankAccountsLoading && unlinkedBankAccountCount > 0 && (
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', marginTop: 4 }}>
                  {unlinkedBankAccountCount} bank account{unlinkedBankAccountCount > 1 ? 's are' : ' is'} not linked to a Chart of Account yet and won't appear here — link {unlinkedBankAccountCount > 1 ? 'them' : 'it'} in Bank Accounts settings.
                </p>
              )}
              {!bankAccountsLoading && payableBankAccounts.length === 0 && (
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', marginTop: 4 }}>
                  No bank account is linked to a Chart of Account yet. Add or link one in Bank Accounts settings first.
                </p>
              )}
            </div>

            <div>
              <label style={labelStyle}>Vendor</label>
              <select
                name="vendorId"
                value={formData.vendorId}
                onChange={handleInputChange}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">None</option>
                {vendorsResponse?.data?.map(vendor => (
                  <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Payee (if not a vendor)</label>
              <input
                type="text"
                name="payeeName"
                value={formData.payeeName}
                onChange={handleInputChange}
                placeholder="e.g., Office Depot, Uber"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Payment Method <span style={{ color: '#c95a5a' }}>*</span></label>
              <select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                style={{ ...inputStyle, cursor: 'pointer' }}
                required
              >
                {EXPENSE_PAYMENT_METHODS.map(method => (
                  <option key={method.value} value={method.value}>{method.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Reference Number</label>
              <input
                type="text"
                name="reference"
                value={formData.reference}
                onChange={handleInputChange}
                placeholder="e.g., Receipt #1234"
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Notes</label>
            <textarea
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Additional notes about this expense..."
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
            <button type="button" onClick={onCancel} style={{ padding: '10px 24px', background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={createExpenseMutation.isPending} style={{ padding: '10px 24px', background: createExpenseMutation.isPending ? 'rgba(92,78,51,0.25)' : 'linear-gradient(135deg, #d97706, #b45309)', border: 'none', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: createExpenseMutation.isPending ? '#c9c2ab' : '#1c2033', cursor: createExpenseMutation.isPending ? 'not-allowed' : 'pointer' }}>
              {createExpenseMutation.isPending ? 'Saving...' : 'Record Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
